(function () {
  'use strict';

  // The badge belongs to the Recharge Subscriptions app block, not to the theme, and it is
  // not reachable from the document: Recharge widget v2 is a Lit web component, so the badge
  // lives inside the (open) shadow root of <recharge-subscription-widget>. Three consequences
  // drive everything below:
  //
  //   1. document.querySelector cannot see it — every shadow root has to be crawled.
  //   2. The host and its shadow root attach after the theme paints, so a one-shot lookup
  //      loses the race. New roots also appear later (rc-benefits, rc-selling-plans, ...).
  //   3. Lit renders the label as a text node preceded by a <!--?lit$...--> marker comment.
  //      Overwriting textContent deletes that marker and detaches Lit's ChildPart, which can
  //      duplicate or drop the label on the next re-render. Writing nodeValue on the existing
  //      text node leaves Lit's bookkeeping intact.
  //
  // Recharge re-renders the widget on variant and selling-plan change, so the override is
  // re-applied from a MutationObserver rather than being written once.
  var BADGE_SELECTOR = '.rc-purchase-option__badge, .rc-subscription-badge';
  var CONFIG_SELECTOR = '[data-rc-badge-override]';
  var MAX_DEPTH = 10;
  var WARMUP_DELAYS = [250, 750, 1500, 3000, 6000];

  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var observer = null;
  var pending = false;

  function replacementText() {
    var config = document.querySelector(CONFIG_SELECTOR);
    return config ? config.getAttribute('data-rc-badge-override') : '';
  }

  // Walks the document plus every open shadow root beneath it.
  function eachRoot(root, callback, depth) {
    callback(root);
    if (depth >= MAX_DEPTH) return;

    var elements = root.querySelectorAll('*');
    for (var i = 0; i < elements.length; i++) {
      if (elements[i].shadowRoot) {
        eachRoot(elements[i].shadowRoot, callback, depth + 1);
      }
    }
  }

  function firstTextNode(el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === 3) return el.childNodes[i];
    }
    return null;
  }

  function setBadgeText(badge, text) {
    var node = firstTextNode(badge);

    if (!node) {
      node = document.createTextNode('');
      badge.appendChild(node);
    }

    // Comparing before writing is what keeps the observer from feeding itself: our own write
    // is a mutation, but the pass it schedules finds the text already correct and stops.
    if (node.nodeValue !== text) node.nodeValue = text;
  }

  function apply() {
    pending = false;

    var text = replacementText();

    eachRoot(document, function (root) {
      // Newly attached shadow roots are invisible to an observer watching only the document,
      // so each root gets its own.
      if (observer && observed && !observed.has(root)) {
        observed.add(root);
        observer.observe(root === document ? document.documentElement : root, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      var badges = root.querySelectorAll(BADGE_SELECTOR);
      for (var i = 0; i < badges.length; i++) {
        var badge = badges[i];

        if (!text) {
          // Only reachable in the theme editor, where the section re-renders without the
          // config element after the merchant clears the setting. Put back what Recharge
          // rendered rather than leaving stale text on screen.
          if (typeof badge.rcBadgeOriginal === 'string') {
            setBadgeText(badge, badge.rcBadgeOriginal);
            delete badge.rcBadgeOriginal;
          }
          continue;
        }

        if (typeof badge.rcBadgeOriginal !== 'string') {
          var original = firstTextNode(badge);
          badge.rcBadgeOriginal = original ? original.nodeValue : '';
        }

        setBadgeText(badge, text);
      }
    }, 0);
  }

  function schedule() {
    if (pending) return;
    pending = true;
    // setTimeout rather than requestAnimationFrame: rAF is suspended outright in hidden
    // tabs, and a PDP opened in a background tab is exactly when Recharge renders the
    // widget unobserved. The badge would then visibly flip from the app's text to the
    // override the moment the shopper focused the tab. Timers are throttled when hidden
    // but never suspended, and there is no layout work here that rAF would batch.
    setTimeout(apply, 0);
  }

  function start() {
    if (!document.body) return;
    observer = new MutationObserver(schedule);
    apply();

    // attachShadow() is not an observable mutation. If Recharge inserts its host element and
    // only then populates a shadow root, the insertion wakes us but the root is still null,
    // and everything after that happens inside a root we never got to observe. Unrelated
    // page mutations usually rescue us; these bounded retries make it deterministic instead.
    for (var i = 0; i < WARMUP_DELAYS.length; i++) {
      setTimeout(schedule, WARMUP_DELAYS[i]);
    }
  }

  // Shopify re-executes section scripts on every theme-editor render, so install the observer
  // once and let later executions just re-apply against the new setting value.
  if (window.rcBadgeOverrideApply) {
    window.rcBadgeOverrideApply();
    return;
  }
  window.rcBadgeOverrideApply = schedule;

  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
